/**
 * ActivationGate — single source of truth for "where should this user
 * be right now?" routing decisions.
 *
 * Locked 2026-06-01 — Onboarding Consolidation Phase A (Step 2).
 *
 * Reads `useActivation()` (which polls /api/auth/me) and redirects the
 * user to the correct screen based on `signup_resume_step` /
 * `activation_state`. Renders nothing — purely a side-effect component
 * mounted globally inside `AppContent`.
 *
 * Redirect rules (matches the legacy `_compute_signup_resume` exactly):
 *
 *   signup_resume_step       →  redirect target
 *   ────────────────────────────────────────────────
 *   "terms-and-policy"       →  /terms-and-policy
 *   "build-experience"       →  /build-experience
 *   "first-login-walkthrough"→  /dashboard (no redirect — overlay handles)
 *   null / "complete"        →  no redirect (render children)
 *
 * Public/auth paths bypass the gate entirely so an unauthenticated user
 * can still reach Gateway, signup, login, terms, etc.
 *
 * This gate runs IN ADDITION TO the existing useEffect in App.js — both
 * are idempotent and converge to the same destination. The legacy
 * useEffect ALSO writes localStorage caches that other components
 * read; deleting it is a later step. Until then, both layers coexist.
 */
import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useActivation } from '@/contexts/ActivationContext';

// Paths that should NEVER be redirected — the destinations themselves,
// plus public landing / auth / super-admin / public-customer surfaces.
const PUBLIC_PATHS = new Set([
  '/',
  '/login',
  '/signup',
  '/nc-signup',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/analytics-login',
  '/terms-and-policy',
  '/welcome-setup',
  '/build-experience',
  '/founders-checklist',
  '/landing',
  '/about',
  '/services',
  '/privacy',
  '/terms',
]);

const PUBLIC_PREFIXES = [
  '/super-admin',          // Super Admin surface has its own auth
  '/remodify/',            // Customer portal (token-link)
  '/customer-portal/',     // Customer portal (token-link)
  '/portal/',              // Generic public portal links
  '/job-tracking',         // Customer tracking link
  '/public-',              // Public estimate / quote views
  '/tools/',               // Tools-only flows have their own routing
  '/payment-success',      // Stripe return
  '/estimate/',            // Public estimate views
  '/c/',                   // V3 per-company login deep link (/c/RD-1234)
];

const _isPublicPath = (pathname) => {
  if (PUBLIC_PATHS.has(pathname)) return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
};

export const ActivationGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, authenticated, signupResumeStep } = useActivation();

  useEffect(() => {
    // Don't fire until context has resolved.
    if (loading) return;
    // Public / unauthenticated surfaces are never gated.
    if (!authenticated) return;
    if (_isPublicPath(location.pathname)) return;

    // Match legacy semantics exactly so we don't ping-pong with the
    // legacy useEffect in App.js. Both layers converge on the same
    // destination.
    if (
      signupResumeStep === 'terms-and-policy' &&
      location.pathname !== '/terms-and-policy'
    ) {
      navigate('/terms-and-policy', { replace: true });
      return;
    }
    if (
      signupResumeStep === 'build-experience' &&
      location.pathname !== '/build-experience' &&
      location.pathname !== '/welcome-setup'
    ) {
      navigate('/build-experience', { replace: true });
      return;
    }
    // Locked 2026-06-01 — NC Gateway access doctrine.
    // NC compliance wizard route. Backend sets resume='nc-onboarding'
    // when contractor_type='network_contractor' AND terms accepted AND
    // nc_onboarding_state.completed is false.
    if (
      signupResumeStep === 'nc-onboarding' &&
      !location.pathname.startsWith('/nc-onboarding')
    ) {
      navigate('/nc-onboarding', { replace: true });
      return;
    }
    // first-login-walkthrough → no redirect needed (overlay renders
    // on /dashboard naturally).
    // null / 'complete' → no redirect.
  }, [
    loading, authenticated, signupResumeStep, location.pathname, navigate,
  ]);

  return null;
};

export default ActivationGate;
